import AttachmentDetails = Office.AttachmentDetails;
import { Logger, Log } from "./Logger";
import EmailAddressDetails = Office.EmailAddressDetails;
import { ITag } from "office-ui-fabric-react";
import { ParsedPair, ParseResult } from "../models/ProjectItemDetails";
import { ITagWithNixEnabled } from "../models/shared/ITagWithNixEnabed";

type unDef = undefined | null;

export type MailboxItem = Office.Item &
    Office.ItemCompose &
    Office.ItemRead &
    Office.MessageRead &
    Office.MessageCompose &
    Office.AppointmentRead &
    Office.AppointmentCompose;

/* istanbul ignore file */
export class OfficeWrapper {
    constructor(public logger: Logger) {}

    logInfo(msg: string) {
        Log.info(msg);
    }

    addHandlerAsync(
        eventType: Office.EventType,
        handler: (type: Office.EventType) => void,
        options?: Office.AsyncContextOptions,
        callback?: (asyncResult: Office.AsyncResult<void>) => void
    ) {
        const callbackFunction = callback ? callback : (asyncResult: Office.AsyncResult<void>) => null;
        Office.context.mailbox.addHandlerAsync(eventType, handler, options || {}, callbackFunction);
    }

    // NOTE: unlike addHandlerAsync, Office.context.mailbox.removeHandlerAsync does not take a handler
    // reference - it removes ALL handlers registered for the given eventType.
    removeHandlerAsync(
        eventType: Office.EventType,
        options?: Office.AsyncContextOptions,
        callback?: (asyncResult: Office.AsyncResult<void>) => void
    ) {
        const callbackFunction = callback ? callback : (asyncResult: Office.AsyncResult<void>) => null;
        Office.context.mailbox.removeHandlerAsync(eventType, options || {}, callbackFunction);
    }

    // NOTE: itemId is not present on items that are being composed (new/reply drafts), so this can be
    // undefined even when currentContextItem is defined. Callers should treat undefined as "unknown"
    // rather than "no item".
    getCurrentItemId(): string | undefined {
        return this.currentContextItem?.itemId;
    }

    displayDialogAsync(
        url: string,
        options: Office.DialogOptions,
        callback: (asyncResult: Office.AsyncResult<Office.Dialog>) => void
    ): void {
        try {
            Office.context.ui.displayDialogAsync(url, options, callback);
        } catch (error) {
            this.logger.error(
                "OfficeWrapper. There was an error with the Office.context.ui.displayDialogAsync() operation",
                error
            );
        }
    }
    removeRoamingSetting(key: string): void {
        Office.context.roamingSettings.remove(key);
    }

    /** Sends a message from a page running inside an Office Dialog back to the page that opened it. */
    messageParent(message: string): void {
        try {
            Office.context.ui.messageParent(message);
        } catch (error) {
            this.logger.error(
                "OfficeWrapper. There was an error with the Office.context.ui.messageParent() operation",
                error
            );
        }
    }

    setRoamingSetting(key: string, value: any): void {
        Office.context.roamingSettings.set(key, value);
    }

    getRoamingSetting(key: string): string | null {
        return Office.context.roamingSettings.get(key);
    }

    saveRoamingSettings(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            Office.context.roamingSettings.saveAsync((result: Office.AsyncResult<void>) => {
                if (result.error || result.status === Office.AsyncResultStatus.Failed) {
                    reject(result.error);
                } else {
                    resolve(result.value);
                }
            });
        });
    }

    get userProfileEmailAddress(): string {
        const mailBox = this.currentMailBox;
        let userEmail;

        try {
            userEmail = window.localStorage.getItem("newforma-email");
        } catch (error) {
            this.logger.info(`Issue getting newforma email from local storage in userProfileEmailAddress: ${error}`);
        }

        if (userEmail) {
            return JSON.parse(userEmail);
        } else {
            return mailBox?.userProfile?.emailAddress ?? "";
        }
    }

    get userProfileDisplayName(): string {
        const mailBox = this.currentMailBox;
        return mailBox?.userProfile?.displayName ?? "";
    }

    /** The mailbox's own identity (no NPC login-email fallback); used as the Microsoft sign-in hint. */
    get mailboxUserEmailAddress(): string {
        return this.currentMailBox?.userProfile?.emailAddress ?? "";
    }

    get userProfileEmailDomain(): string {
        const emailDomain = this.userProfileEmailAddress?.split("@");
        return emailDomain?.length ?? 0 > 1 ? emailDomain[1] : "";
    }

    getSender(): EmailAddressDetails {
        const sender = this.currentContextItem?.sender;

        if (!sender) {
            return { emailAddress: "", displayName: "", appointmentResponse: "", recipientType: "" };
        }
        return sender;
    }

    getSenderEmailAddress(): string {
        return this.currentContextItem?.sender?.emailAddress ?? "";
    }

    getContextMailboxItemSenderEmailDomain(): string {
        const emailAddress = this.currentContextItem?.sender?.emailAddress;
        const parts = emailAddress?.split("@");
        return parts?.length > 0 ? parts[1] : "";
    }

    getCurrentEmailSubject(): Promise<string> {
        const item = this.currentContextItem;
        const subject = item ? item.subject : "";
        if (typeof subject === "string" || (subject as any) instanceof String) {
            return Promise.resolve(subject);
        } else {
            return new Promise<string>((resolve, reject) => {
                (subject as any).getAsync((getSubjectResult: any) => {
                    if (!getSubjectResult.error) {
                        this.logInfo(`getCurrentEmailSubject ${getSubjectResult.value}`);
                        resolve(getSubjectResult.value);
                    } else {
                        reject(getSubjectResult.error);
                    }
                });
            });
        }
    }

    get currentMailBox(): Office.Mailbox {
        return Office.context.mailbox;
    }

    /** False inside Office Dialog windows (login, SharePoint consent), which have no mailbox. */
    hasMailboxContext(): boolean {
        return !!Office.context?.mailbox;
    }

    get currentContextItem(): MailboxItem {
        const item = Office.context.mailbox?.item;
        if (!item) {
            this.logInfo("currentContextItem: current item is undefined");
        }
        return item as MailboxItem;
    }

    get currentConversationId(): string {
        const item = this.currentContextItem;
        return item?.conversationId ?? "";
    }
    get currentSenderEmailAddress(): string {
        const item = this.currentContextItem;
        return item?.sender?.emailAddress ?? "";
    }

    getCurrentContextItem(): MailboxItem {
        return this.currentMailBox.item as MailboxItem;
    }
    async getCurrentEmailBody(): Promise<string> {
        const item = this.currentContextItem;
        if (!item) {
            throw new Error("Current context item is undefined.");
        }

        const body = item.body;
        this.logInfo("getCurrentEmailBody - begin");

        try {
            if (typeof body === "string") {
                // If the body is already a string, return it directly
                return body;
            } else {
                // If the body is not a string, use getAsync to retrieve it
                const asyncResult = await new Promise<Office.AsyncResult<string>>((resolve, reject) => {
                    body.getAsync(Office.CoercionType.Text, {}, (result: Office.AsyncResult<string>) => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            reject(result.error);
                        } else {
                            this.logInfo("getCurrentEmailBody - Email successfully retrieved");
                            resolve(result);
                        }
                    });
                });

                // Return the body text from the async result
                return asyncResult.value;
            }
        } catch (error) {
            Log.error("Error retrieving email body:", error);
            throw error; // Re-throw the error to propagate it
        } finally {
            this.logInfo("getCurrentEmailBody - end");
        }
    }

    getCurrentEmailAttachmentDetails(): AttachmentDetails[] {
        const item = this.currentContextItem;
        return item?.attachments ?? [];
    }

    getCurrentEmailToRecipients(): EmailAddressDetails[] {
        const item = this.currentContextItem;
        return item?.to ?? [];
    }

    getCurrentEmailCcRecipients(): EmailAddressDetails[] {
        const item = this.currentContextItem;
        return item?.cc ?? [];
    }

    getFromPersonaProps() {
        const senderEmail = this.getSenderEmailAddress();
        return senderEmail ? [{ text: senderEmail }] : [];
    }

    getToPersonaProps() {
        return this.getCurrentEmailToRecipients()
            .map((recipient) => recipient.emailAddress)
            .filter((email) => !email.toLowerCase().endsWith("newforma.email"))
            .map((email) => ({ text: email }));
    }

    getCCPersonaProps() {
        return this.getCurrentEmailCcRecipients()
            .map((recipient) => recipient.emailAddress)
            .filter((email) => !email.toLowerCase().endsWith("newforma.email"))
            .map((email) => ({ text: email }));
    }

    getCurrentEmailDate(): Date {
        const item = this.currentContextItem;
        return item?.dateTimeCreated ?? new Date(12, 31, 1999);
    }

    isMailboxVersionSupported(versionToCheck: string): boolean {
        return Office.context.requirements.isSetSupported("mailbox", versionToCheck);
    }

    /**
     * True if this Office host supports Nested App Authentication (NAA), i.e. MSAL's
     * `createNestablePublicClientApplication` can broker sign-in natively without the
     * Office Dialog fallback. NAA brokers both work/school and personal Microsoft accounts;
     * it is unavailable on older hosts and for mailboxes with no Microsoft identity (e.g. Gmail),
     * so callers must always keep a non-NAA fallback.
     */
    isNestedAppAuthSupported(): boolean {
        return Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
    }

    async getInternetHeader(headerName: string): Promise<string | null> {
        const mailboxItem = this.currentContextItem;
        if (!mailboxItem) {
            throw new Error("Mailbox item is undefined.");
        }

        try {
            const asyncResult = await new Promise<Office.AsyncResult<Office.InternetHeaders>>((resolve, reject) => {
                mailboxItem.internetHeaders.getAsync(
                    [headerName], // First parameter: array of header names
                    {}, // Second parameter: options object (can be empty)
                    (result) => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            reject(asyncResult.error);
                        } else {
                            this.logInfo(`getInternetHeader ${result.value}`);
                            resolve(result);
                        }
                    }
                );
            });

            // Extract the header value from the result
            const headerValue = asyncResult.value ? (asyncResult.value as any)[headerName] : null;
            return headerValue;
        } catch (error) {
            Log.error("Error retrieving internet header:", error);
            return null;
        }
    }

    // recoded
    async removeInternetHeaders(headerNames: string[]): Promise<void> {
        const item = this.currentContextItem;

        if (!item) {
            throw new Error("Mailbox item is undefined.");
        }

        const headersToRemove: { [key: string]: string } = {};
        headerNames.forEach((name) => {
            headersToRemove[name] = "";
        });

        try {
            await item.internetHeaders?.setAsync(headersToRemove);
        } catch (error) {
            Log.error("removeInternetHeaders - internetHeaders is unavailable (item is not in compose mode).", error);
        }
    }

    async setInternetHeaders(headers: Object): Promise<void> {
        const item = this.currentContextItem;
        if (!item) {
            return Promise.reject(new Error("Mailbox item is undefined."));
        }

        try {
            await item.internetHeaders?.setAsync(headers);
        } catch (error) {
            Log.error("setInternetHeaders - internetHeaders is unavailable (item is not in compose mode).", error);
        }
    }

    convertItemIdToRestId(itemId: string): string {
        const mailBox = this.currentMailBox;
        return mailBox?.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
    }

    convertToRestId(item: MailboxItem): string {
        const itemId = item?.itemId ?? "";

        return this.convertItemIdToRestId(itemId); // Office.context.mailbox.convertToRestId( itemId, Office.MailboxEnums.RestVersion.v2_0);
    }

    getCurrentMessageNrn(): string {
        const item = this.currentContextItem;
        const restId = this.convertItemIdToRestId(item?.itemId ?? "");
        return `nrn:email:message:ms-graph:${restId}`;
    }

    getRestUrl(): string {
        const mailBox = this.currentMailBox;
        return mailBox?.restUrl ?? "";
    }

    getLatestSupportedOfficeApiVersion(): string | null {
        let setIsSupported = true;
        let minorVersion = 0;
        while (setIsSupported) {
            setIsSupported = Office.context.requirements.isSetSupported("mailbox", `1.${minorVersion}`);
            if (!setIsSupported) {
                minorVersion--;
                break;
            }
            minorVersion++;
        }

        return minorVersion >= 0 ? `1.${minorVersion}` : null;
    }

    getClientPlatform(): string {
        const platform = Office.context.diagnostics.platform;

        switch (platform) {
            case Office.PlatformType.Android:
                return "Android";
            case Office.PlatformType.iOS:
                return "iOS";
            case Office.PlatformType.Mac:
                return "Mac";
            case Office.PlatformType.OfficeOnline:
                return "Office Online";
            case Office.PlatformType.PC:
                return "Windows";
            case Office.PlatformType.Universal:
                return "WinRT";
            default:
                return "unknown";
        }
    }

    getClientVersion(): string {
        return Office.context.diagnostics.version;
    }

    getTimezone(): string {
        return Office.context.mailbox.userProfile.timeZone;
    }

    getCurrentMessageId(): string {
        const mailBox = this.currentMailBox;
        const itemId = mailBox.item?.itemId ?? "";

        if (mailBox.diagnostics.hostName === "OutlookIOS") {
            // itemId is already REST-formatted.
            return itemId;
        } else {
            // Convert to an item ID for API v2.0.
            return mailBox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
        }
    }

    async getCcAsync(): Promise<EmailAddressDetails[]> {
        const cc: any = this.currentContextItem?.cc ?? [];

        if (cc instanceof Array) {
            return Promise.resolve(cc);
        }
        return new Promise((resolve, reject) => {
            cc.getAsync((result: any) => {
                if (result.error) {
                    this.logger.error(
                        "OfficeWrapper. There was an error retrieving the 'cc' recipients of the email",
                        result.error
                    );
                    reject(result.error);
                }
                this.logInfo(`getCcAsync ${result.value}`);
                resolve(result.value);
            });
        });
    }

    async getToAsync(): Promise<EmailAddressDetails[]> {
        // if defined as Const... the compiler has issues
        const to: any = this.currentContextItem?.to ?? ([] as EmailAddressDetails[]);

        if (to instanceof Array) {
            return Promise.resolve(to);
        }

        return new Promise((resolve, reject) => {
            to.getAsync((result: any) => {
                if (result.error) {
                    this.logger.error(
                        "OfficeWrapper. There was an error retrieving the 'to' recipients of the email",
                        result.error
                    );
                    reject(result.error);
                }
                resolve(result.value);
            });
        });
    }

    // das
    async setToAsync(emails: EmailAddressDetails[]): Promise<void> {
        // if defined as Const... the compiler has issues
        const to: any = this.currentContextItem?.to;
        return new Promise((resolve, reject) => {
            to?.setAsync(emails, (result: any) => {
                if (result.error) {
                    this.logger.error("OfficeWrapper. There was an error updating the 'to' recipients", result.error);
                    reject(result.error);
                }
                resolve(result.value);
            });
        });
    }
    // das
    async setCcAsync(emails: EmailAddressDetails[]): Promise<void> {
        // if defined as Const... the compiler has issues
        const cc: any = this.currentContextItem?.cc;
        return new Promise((resolve, reject) => {
            // some strangeness here.. there doesn't seem to be a 'setAsync on to
            cc?.setAsync(emails, (result: Office.AsyncResult<void>) => {
                if (result.error) {
                    this.logger.error("OfficeWrapper. There was an error updating the 'cc' recipients", result.error);
                    reject(result.error);
                }
                resolve(result.value);
            });
        });
    }

    isReadMode(): boolean {
        const item = Office.context.mailbox.item;

        return !!item?.itemId;
    }

    parseEmailSubject(
        project: ITag | null,
        subject: string,
        projects: ITagWithNixEnabled[]
    ): { itemType?: "Submittals" | "RFIs" | "Action Items"; item?: string } {
        if (typeof subject !== "string" || !subject.trim() || project == null) {
            return {};
        }
        const projectResults = this.parseSubjectGetLast(subject);
        const lastProject = projectResults.lastPair?.leftToken;
        const projectCode = projectResults.lastPair?.projectCode;
        // "For a project name in the format 'number - name', the name is extracted and any special characters are converted into escaped characters"
        const projectName = project?.name.replace(/^[^-]*-\s*/, "");
        const escapedProject = projectName?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

        // Match: project name, then " - " (space-hyphen-space), then your keywords
        const regex = new RegExp(
            `\\b${
                lastProject ? lastProject : escapedProject
            }\\b\\s*-\\s*(Submittal|RFI|Action\\s?Item):\\s*([^:]+)(?=:)`,
            "gi"
        );
        let match = subject.match(regex);
        let lastMatch = null;

        while ((match = regex.exec(subject)) !== null) {
            lastMatch = match;
        }

        // needed to prevent further prefilling if project is not in user's project list
        const isProjectInList = projects.find((proj) => proj.code === projectCode);

        if (!lastMatch || !isProjectInList) {
            return {};
        }
        const map: Record<string, "Submittals" | "RFIs" | "Action Items"> = {
            submittal: "Submittals",
            rfi: "RFIs",
            actionitem: "Action Items",
        };

        const keyword = lastMatch[1].replace(/\s+/g, "").toLowerCase();
        const itemType = map[keyword];

        const fullItem = lastMatch[2]?.trim();

        const item = fullItem?.split(":")[0];

        return itemType && item ? { itemType, item } : {};
    }

    parseSubjectGetLast(subject: string): ParseResult {
        // Use a flexible splitter that works with varying spaces around hyphens
        const tokens = subject
            .trim()
            .split(/\s+-\s+/g)
            .map((t) => t.trim())
            .filter(Boolean);

        const keywordMatchers: Record<string, RegExp> = {
            RFI: /(^|\s)RFI:/i,
            Submittal: /(^|\s)Submittal:/i,
            ActionItem: /(^|\s)ActionItem:/i,
        };

        const keywords: string[] = ["RFI", "Submittal", "ActionItem"];
        const allPairs: ParsedPair[] = [];

        // Forward scan, record every "<left> - <keywordBlock>" pair we see.
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            const matchedKw = keywords.find((kw) => keywordMatchers[kw].test(token));
            if (!matchedKw) {
                continue;
            }

            const leftIdx = i - 1;
            if (leftIdx < 0) {
                continue;
            } // No left token -> cannot construct a pair

            const pair: ParsedPair = {
                keyword: matchedKw,
                leftToken: tokens[leftIdx],
                rightToken: token,
                combined: `${tokens[leftIdx]} - ${token}`.toLowerCase(),
                index: i,
                projectCode: token.split(":").pop() || "",
            };
            allPairs.push(pair);
        }

        // Last pair is the final element of allPairs.
        const lastPair = allPairs.length ? allPairs[allPairs.length - 1] : undefined;
        const lastProjectName = lastPair?.leftToken;
        this.logger.info(`Prefilling project, lastProjectName: ${lastProjectName}`);
        return { allPairs, lastPair, lastProjectName };
    }
}
